<template>
<!-- 头部 -->
<div v-if="isMobile">
  <slot name="search-header" :searchFn="handlePromotionSearch">
    <KHeader
      :isShowCart="true"
      :placeholder-tip="searchHeaderTip"
      @click="handleSearchBarClick"
    />
  </slot>

  <div class="search-header">
    <KSearchFilterBar 
      :defaultParams="currentFilterParams"
      :filter="searchResult.filter || []"
      @search:change="handleSearchClick"
    />
  </div>

  <div class="product-list-wrapper" v-show="showResult">
    <van-pull-refresh :modelValue="refreshing" @refresh="onRefresh">
      <van-list
        :loading="loadingMore"
        :finished="finished"
        finished-text="No more"
        :offset="200"
        @load="onLoad"
      >
        <template v-if="searchResult.keywordsSuggestions?.length">
          <p
            v-if="searchResult.suggestionUsed"
            class="correction-words-tips"
          >
            <van-icon name="info-o" color="#ff976a" />&nbsp;Showing results for&nbsp;
            <span class="first-word">{{ searchResult.keywordsSuggestions[0] }}  
            </span>.&nbsp;Search instead for&nbsp;<span class="last-word" @click="handleCorrectionWordClick(searchParams.keywords || '', 1)">{{ searchParams.keywords }}</span>
          </p>
          <p
            v-else
            class="correction-words-tips"
          >
            <van-icon name="info-o" />&nbsp;Showing results for&nbsp;
            <span class="first-word">{{ searchParams.keywords }}</span>.&nbsp;Did you mean:
            <span class="last-word" @click="handleCorrectionWordClick(searchResult.keywordsSuggestions[0], 2)">{{ searchResult.keywordsSuggestions[0] }}</span>
          </p>  
        </template>
        
        <div class="category-div" v-if="categoryList.length > 0">
          <KFrontCategorys
            :current-cid="(searchParams.frontCategoryId! as number)"
            :category-list="categoryList"
            @update:cid="handleCategoryClick"
          />
        </div>

        <!-- 类目搜索不显示关键词标签 -->
        <div
          v-if="!searchParams.frontCategoryId && searchResult.labels?.length > 0"
          class="labels"
        >
          <KSearchLabels :labels="searchResult.labels" />
        </div>
        
        <div class="product-list">
          <div 
            class="product" 
            v-for="item in searchResult.result"
            :key="item.isAds ? `${item.adsIndex}_${item.listingId}` : item.listingId"
          >
            <KProductItem
              :type="isPromotion ? 'promotion' : 'info'"
              :productData="item"
              :source="productListingSource"
              :isAds="item.isAds"
              showRate
              @add-cart:click="onAddCart"
            />
          </div>
        </div>
      </van-list>
    </van-pull-refresh>
  </div>
</div>

<div v-else-if="isPromotion">
  <PCPromotionListing
    :finished="finished"
    :promotionListings="searchResult.result"
    :loading="loading"
    :tips="promotionTips || ''"
    :filter="searchResult.filter || []"
    :category-list="categoryList || []"
    :sort-list="sortList"

    @search="handleSearchClick"
    @load="onLoad"
    @add-cart:success="emits('add-cart:success')"
  />
</div>
<div v-else>
  <PCSearchResult 
    ref="PCSearchResultRef"
    :filter="searchResult.filter || []"
    :showResult="showResult"
    :categoryList="categoryList"
    :listings="searchResult.result || []"
    :ads="searchResult.ads"
    :suggestion="{ suggestionUsed: searchResult.suggestionUsed, keywordsSuggestions: searchResult.keywordsSuggestions}"
    :max-page="maxPage"
    :current-page="currentPage"
    :page-size="searchParams.size"
    :keyword="searchParams.keywords || ''"
    :loading="loading"
    :breadcrumbs="breadcrumbs"
    :h1Tips="h1Tips"
    :sort-list="sortList"

    @filter:change="handleSearchClick"
    @filter:paginate="handlePCFilterPaginateChange"
    @filter:sort="handlePCFilterSortChange"
    @filter:keyword="handlePCFilterKeywordChange"
    @filter:suggestion-keyword="handleCorrectionWordClick"
  />
</div>
</template>

<script setup lang="ts">
import { type ISearchParams, search, searchByStoreId, getFrontCategoryById } from '@/api/models/system'
import { showConfirmDialog } from 'vant'
import { useRouter } from 'vue-router'
import { type ILabel } from '@/components/KSearch/Labels.vue'
import PCSearchResult from '@/pages-pc/SearchResult.vue'
import PCPromotionListing from '@/pages-pc/PromotionListings/index.vue'
import { getCategoryPathById, getFrontCategoryPathById } from '@/api/models/product'
import type { IBreadcrumbs } from '~/components/PC/Breadcrumbs.vue'
import { customEventFn, eventName, type SearchEventParams } from '@/plugins/bi-sa.client'
import { sourceToPipeParts } from '@/utils/traffic-source'
import {
  buildCrossStoreSpendSaveListingSource,
  buildStoreSearchListingSource,
  resolveCrossStoreSpendSaveFirstPage,
  resolveCrossStoreSpendSaveSourceValue
} from '@/plugins/utils'
import { v4 as uuidv4 } from 'uuid'

type searchType = 'refresh' | 'load-more' | 'pc' | 'pc-refresh'

interface IProps {
  isPromotion?: boolean,
  promotionId?: number
  showDiscountInfo?: boolean
  promotionTips?: string,

  onAddCart?: (productItem: any) => void
}
export interface ICategory { id: number, name: string, subCategories?: ICategory, bindUrl: string }
export interface ISearchResult {
  filter: any,
  result: Array<any>,
    labels: ILabel[]
    total: number,
    sessionId: number,
    suggestionUsed: boolean,
  keywordsSuggestions: string[],
  [key: string]: any
}
export interface ISort { text: string, value: '' | 1 | 2 | 4 | 9 }
const router = useRouter()
const { $filterEmptyParams, $SENSORS_EVENT, $sensorsTrack,$formatStringToURL } = useNuxtApp()

definePageMeta({ layout: 'wap' })
const sortList = ref<ISort[]>([
  { text: 'Popular', value: '' },
  { text: 'Top Sale', value: 4 },
  { text: 'Latest', value: 9 },
  { text: 'Low to High', value: 1 },
  { text: 'High to Low', value: 2 },
])
// 如果是 Promotion 跨店满减搜索
const props = defineProps<IProps>()
const emits = defineEmits<{ (e: 'add-cart:success'): void }>()

const route = useRoute()
const isMobile = useIsMobile()
const categoryList = ref<ICategory[]>([])
const {
  id, storeCategoryIds, brandIds,
  storeId, form, backCid, source, correctionWords,externalLastTrafficSourceType,
  q: keyword,
  page='1'
} = route.query as any

if(!keyword && !id && !brandIds && !storeId && !backCid && !props.isPromotion) {
  throw new Error('No search keyword')
}

let size =  isMobile.value ? 18 : 36
if(props.isPromotion) {
  size = 20
}
const searchParams = reactive<ISearchParams>({ 
  skip: (page - 1) * size,
  size,
  keywords: keyword,
  storeCategoryIds: storeCategoryIds ? Number(storeCategoryIds) : '',
  storeId: storeId ? Number(storeId) : '',
  brandIds: brandIds ? Number(brandIds) : '',
  frontCategoryId: route.query.id ? Number(String(route.query.id)) : '',
  useSuggestion: !correctionWords
})
if(props.isPromotion) {
  searchParams.promotionId = props.promotionId
  searchParams.sortBy = 8
}
const trackFn = (params: {
  listing_id: number
  sku_id: number,
  productTitle: string
  productPrice: number
}) => {
  const [
    internalLatestTrafficSourceModule = '',
    internalLatestTrafficSourceType = '',
    insideLatestTrafficSourceValue = ''
  ] = (source || '').split('|')  
  const trackData = {
    ...params,
    isAd: true,
    internalLatestTrafficSourceModule,
    internalLatestTrafficSourceType,
    insideLatestTrafficSourceValue
  }
  
  $sensorsTrack($SENSORS_EVENT.exposureProduct, trackData)
}

const searchHeaderTip = storeId ? 'Search in store' : 'I am searching for...'
// 缓存上一次的筛选条件
const currentFilterParams = ref<ISearchParams>({})
const searchResult = ref<ISearchResult>({ filter: [], result: [], total: 0, sessionId: -1, labels: [], keywordsSuggestions: [], suggestionUsed: true })

/** 接口自动纠错（suggestionUsed）时，进商详 source_type 应为 correctionSearch */
const buildProductListingSource = (
  routeSource: string,
  suggestionUsed: boolean,
  keywordsSuggestions: string[] = []
) => {
  const rawSource = decodeURIComponent(routeSource || '')
  const [module, , sourceValue, firstPage, latestPage] = sourceToPipeParts(rawSource)

  if (suggestionUsed && keywordsSuggestions.length) {
    return [
      module || 'searchProduct',
      'correctionSearch',
      keywordsSuggestions[0] || sourceValue,
      firstPage || 'searchProductResults',
      latestPage || 'searchProductResults'
    ].join('|')
  }

  return rawSource
}

const isStoreSearch = computed(() => Boolean(searchParams.storeId || route.query.storeId))

const resolveProductListingSource = (
  suggestionUsed: boolean,
  keywordsSuggestions: string[] = []
) => {
  if (isStoreSearch.value) {
    const keyword = String(route.query.q || searchParams.keywords || '')
    return buildStoreSearchListingSource(keyword, route, { suggestionUsed, keywordsSuggestions })
  }

  return buildProductListingSource(
    String(route.query.source || ''),
    suggestionUsed,
    keywordsSuggestions
  )
}

const buildPromotionProductListingSource = (filter = searchResult.value.filter) =>
  buildCrossStoreSpendSaveListingSource(
    resolveCrossStoreSpendSaveSourceValue(
      searchParams.keywords,
      currentFilterParams.value.categoryIds,
      filter
    ),
    resolveCrossStoreSpendSaveFirstPage(route)
  )

const productListingSource = computed(() => {
  if (props.isPromotion) {
    return buildPromotionProductListingSource()
  }

  return resolveProductListingSource(
    searchResult.value.suggestionUsed,
    searchResult.value.keywordsSuggestions || []
  )
})

const handleCategoryClick = async (fcid: number) => {
  searchParams.frontCategoryId = searchParams.frontCategoryId === fcid ? Number(id) : fcid
  isR18Show = false

  await handleSearchClick(searchParams, 'refresh')
}

const loadingMore = ref<boolean>(false) // 加载更多
const loading = ref<boolean>(false) // 是否在加载
const refreshing = ref<boolean>(false) // 刷新
const finished = ref<boolean>(false) // 是否加载完成
const onRefresh = async() => {
  await handleSearchClick(currentFilterParams.value)
}
const onLoad = async() => {
  if(loadingMore.value || loading.value) {
    return
  }
  loadingMore.value =  true
  await handleSearchClick(currentFilterParams.value, 'load-more')
}

const handleSearchBarClick = () => {
  const query = { ...route.query } as Record<string, unknown>
  delete query.correctionWords

  // 凑单页面进来，在当页进行筛选
  if(props.isPromotion) {
    return
  }
  // 店铺内搜索保留入口 first_page（cart / productDetail 等），全站搜索才设为 searchProductResults
  if (!isStoreSearch.value) {
    query.first_page = 'searchProductResults'
  }
  router.replace({ path: '/search-input', query })
}

const maxPage = ref<number>(1)
const currentPage = ref<number>(Math.ceil(searchParams.skip! / size) + 1)

const showResult = ref<boolean>(true)
// 标记 成年提示 是否已经弹出过
let isR18Show: boolean = false
const handleSearchClick= async(params: ISearchParams={}, type:searchType = 'refresh') => {
  if(loading.value) {
    return
  }
  
  if(type === 'refresh') {
    searchParams.skip! = 0
    currentPage.value = 1
    setPCQueryPage(1)
    refreshing.value = true
  }

  currentFilterParams.value = params
  const _searchP = { ...searchParams,...params, limit: searchParams.size }

  if(!params.categoryIds && backCid) {
    _searchP.categoryIds = backCid ? backCid.split('_') : []
  }

  // 类目页：有 Suggestions 子类目时只传最后一个 frontCategoryId，否则传 URL id
  const routeCategoryId = route.query.form === 'category' && route.query.id
    ? Number(String(route.query.id))
    : null
  if (routeCategoryId) {
    const pf = params.frontCategoryId
    let suggestionIds: number[] = []
    if (Array.isArray(pf)) {
      suggestionIds = pf.filter((fid) => Number(fid) !== routeCategoryId)
    } else if (pf !== undefined && pf !== '' && pf !== null && Number(pf) !== routeCategoryId) {
      suggestionIds = [Number(pf)]
    }
    _searchP.frontCategoryId = suggestionIds.length
      ? suggestionIds[suggestionIds.length - 1]
      : routeCategoryId
    searchParams.frontCategoryId = routeCategoryId
  }

  loading.value = true
  const _p = $filterEmptyParams(_searchP, true)
  let _sfn: any = search
  if(_p.storeId) {
    _sfn = searchByStoreId

    // 店铺搜索不支持
    delete searchParams.useSuggestion
  }

  if(props.isPromotion) {
    if(!_p.sortBy) {
      _p.sortBy = 8
    }
  }

  const res = await _sfn(_p)
  const currentRequestId = uuidv4()
  const algorithmModelName = res.algorithmModelName || ''
  // BI 埋点
  const { searchEventFn } = await import('@/plugins/bi-sa.client')
  const [
    sourceModule = '',
    searchSourceTypeFromSource = '',
    sourceValueFromSource = ''
  ] = sourceToPipeParts(typeof source === 'string' ? decodeURIComponent(source) : '')
  let searchSourceType = searchSourceTypeFromSource || 'enterSearch'
  const categoryName = sourceValueFromSource || 'enterSearch'
  const defaultSearchModule = searchParams.frontCategoryId ? 'searchCategory' : 'searchProduct'
  // 添加排序类型转换函数
  const getSortType = (sortBy?: number) => {
    switch (sortBy) {
      case 9: return 'latest'
      case 4: return 'topSales'
      case 1: return 'priceLowToHigh'
      case 2: return 'priceHighToLow'
      default: return 'bestMatch'
    }
  }
  showResult.value = isR18Show ? true : !res.isR18
  const listingSource = props.isPromotion
    ? buildPromotionProductListingSource(res.filter)
    : resolveProductListingSource(
      res.suggestionUsed,
      res.keywordsSuggestions || []
    )
  // 敏感类目 成年提示信息 只弹出一次
  if(res.isR18 && !isR18Show) {
    showConfirmDialog({
      title: 'JUST CHECKING',
      message:
        'By agreeing to view this link, you certufy that you are 18 years and above.',
      cancelButtonText: 'NO, I\'M NOT 18',
      confirmButtonText: 'Yes, I am 18 or older'
    })
      .then(() => {
        showResult.value = true
        isR18Show = true
      })
      .catch(() => {
        router.back()
      })
  }

  // 店铺搜索 ads / filter 为 undefined
  const { result, total, filter, ads: adsResult = [] } = res

  const adsMap: IAnyObject = {}
  const startIdx = type === 'pc'
    ? (currentPage.value - 1) * searchParams.size!
    : searchParams.skip!
  
  adsResult.forEach((ads: Search.IAds) => {
    !adsMap[ads.index] && (adsMap[ads.index] = { isHandle: false, list: [] })

    const { defaultSkuId, listingId, minPrice, title } = ads.ad_content.listing

    if(process.client) {
      trackFn({
        listing_id: listingId,
        sku_id: defaultSkuId,
        productTitle: title,
        productPrice: minPrice
      })
    }
    
    adsMap[ads.index].list.push(ads)
  })

  const list: Search.IListing[] = []
  result.forEach((c: Search.IListing, idx: number) => {
    const keys = `${idx + 1 + startIdx}`

    const _adsItem = adsMap[keys]
    if(_adsItem) {
      const { list: _adsList } = _adsItem
      _adsItem.isHandle = true

      if(_adsList?.length > 0) {
        list.push(..._adsList.map((_ads: any) => ({
          isAds: true,
          adsIndex: idx + 1,
          ..._ads.ad_content.listing,
          requestId: currentRequestId,
          algorithmModelName,
          source: listingSource
        })))
        if (_adsList[0].ad_content && _adsList[0].ad_content.listing) {
          try{
            const itemInfo = _adsList[0].ad_content.listing || {}
            // BI埋点-广告曝光
            const [
              internalLatestTrafficSourceModule = '',
              internalLatestTrafficSourceType = '',
              insideLatestTrafficSourceValue = '',
              internalFirstTrafficSourcePage = '',
              internalLatestTrafficSourcePage = ''
            ] = (source || '').split('|')
            const exposureProductParams = {
              internal_latest_traffic_source_module: internalLatestTrafficSourceModule,
              internal_latest_traffic_source_type: internalLatestTrafficSourceType,
              inside_latest_traffic_source_value: insideLatestTrafficSourceValue,
              internal_first_traffic_source_page: internalFirstTrafficSourcePage,
              internal_latest_traffic_source_page: internalLatestTrafficSourcePage,
              listing_id: itemInfo.listingId,
              sku_id: itemInfo.defaultSkuId,
              position: route.query.page ? parseInt(route.query.page as string) : 1,
              product_title: itemInfo.title,
              product_price: itemInfo.minPrice,
              is_ad: true
            }
            customEventFn(eventName.exposureProduct,  exposureProductParams)
          } catch(e) {
            console.error(e)
          }
        }
      }
    }

    list.push({ ...c, requestId: currentRequestId, algorithmModelName, source: listingSource })
  })

  if(['refresh', 'pc'].includes(type)){
    searchResult.value = { ...res, result: list }
  } else if(type === 'load-more') {
    Object.assign(searchResult, { total, filter })
    searchResult.value.result!.push(...list)
  }

  maxPage.value = Math.ceil(searchResult.value.total / searchParams.size!)
  if((type !== 'pc' && isMobile.value) || props.isPromotion) {
    searchParams.skip! += searchParams.size!
  }
  
  // 是否加载完成
  finished.value = searchParams.size! > res.result.length
  
  if(finished.value) {
    Object.keys(adsMap).forEach((k: any, idx: number) => {
      const item = adsMap[k]

      if(Number(k) > startIdx && !item.isHandle) {
        const adsIndex = searchResult.value.result.length + idx + 1
          
        searchResult.value.result.push(...item.list.map((_ads: any) => ({
          isAds: true,
          adsIndex,
          ..._ads.ad_content.listing,
          requestId: currentRequestId,
          algorithmModelName,
          source: listingSource
        })))
      }    
    })
  }
  loadingMore.value = false
  loading.value = false
  refreshing.value = false

  const saBiParams: SearchEventParams = {
    internal_latest_traffic_source_module: (sourceModule || defaultSearchModule) as SearchEventParams['internal_latest_traffic_source_module'],
    internal_latest_traffic_source_type: searchSourceType as SearchEventParams['internal_latest_traffic_source_type'],
    inside_latest_traffic_source_value: searchParams.keywords || categoryName || '',
    request_id: currentRequestId,
    algorithm_model_name: algorithmModelName,
    is_has_result: res.total && res.total > 0,
    result_nums: res.total || 0,
    is_has_filter:  (() => {
      // 检查所有属性是否有效
      const validProps = Object.entries(_p).filter(([key, value]) => {
        if (value === undefined || value === null || value === '' || value === '[]') {
          return false;
        }
        if (Array.isArray(value)) {
          return value.length > 0;
        }
        if (typeof value === 'object' && value !== null) {
          return Object.keys(value).length > 0;
        }
        return true;
      });

      return validProps.length > 5;
    })(),
    sort_type: getSortType(_p.sortBy)
  }
  if (res.keywordsSuggestions && res.keywordsSuggestions.length) {
    // 存在纠错词, 单独再埋点一次
    const _saBiParams = { ...saBiParams }
    _saBiParams.internal_latest_traffic_source_type = 'correctionSearch'
    _saBiParams.inside_latest_traffic_source_value = res.keywordsSuggestions[0]
    searchEventFn(_saBiParams, $sensorsTrack)
  }
  if (form === 'category-hot') {
    // PC热门分类
    saBiParams.internal_latest_traffic_source_type = 'hotCategory'
    saBiParams.internal_latest_traffic_source_module = 'searchCategory'
    saBiParams.inside_latest_traffic_source_value = Array.isArray(route.params.categoryName)
      ? route.params.categoryName[0]
      : route.params.categoryName
  }
  searchEventFn(saBiParams, $sensorsTrack)
}

const handlePromotionSearch = async(keyword: string='') => {
  searchParams.keywords = keyword

  await handleSearchClick({}, 'refresh')
}

const initFrontCategory = async() => {
  if(searchParams.frontCategoryId) {
    const ctype = form === 'category-hot' ? 2 : 1
    const res = await getFrontCategoryById(searchParams.frontCategoryId as number, ctype, false)
    
    categoryList.value = res || []
  }
}
onMounted(async() => {
  await initFrontCategory()

  if(isMobile.value) {
    const khbar = document.getElementsByClassName('k-header-bar')[0]
    const shbar = document.getElementsByClassName('search-header')[0]
    const pdlist = document.getElementsByClassName('product-list-wrapper')[0]

    if(khbar && shbar && pdlist) {
      const khbarH = khbar.clientHeight - 1
      const shbarH = shbar.clientHeight - 1

      shbar.setAttribute('style', `top: ${khbarH}px`)
      pdlist.setAttribute('style', `padding-top: ${khbarH + shbarH + 8}px;padding-bottom: 50px`)
    }
  }
})

/**
 * 
 * @param keyword 关键词
 * @param type 1：搜索关键词，2：搜索纠错词
 */
const handleCorrectionWordClick = async(keyword: string, type: 1 | 2) => {
  const query = route.query
  if(type === 1) {
    query.correctionWords = searchParams.keywords || ''
  } else {
    delete query.correctionWords
  }

  const [module, sourceType, , firstPage, latestPage] = sourceToPipeParts(
    decodeURI((query.source || '') as string)
  )

  query.source = [
    module || 'searchProduct',
    type === 2 ? 'correctionSearch' : (sourceType || 'enterSearch'),
    keyword,
    firstPage || 'searchProductResults',
    latestPage || 'searchProductResults'
  ].join('|')

  const newUrl = router.resolve({ path: route.path, query: { ...query, q: keyword, page: 1 } }).href
  location.replace(newUrl)
}


/** ------ PC START ----------- */
const setPCQueryPage = (page: number) => {
  const routeQuery = route.query as any
  const newQuery = JSON.parse(JSON.stringify(routeQuery))
  newQuery.page = page
  router.replace({ path: route.path, query: newQuery })
}
const handlePCFilterPaginateChange = (page: number) => {
  setPCQueryPage(page)  

  currentPage.value = page
  searchParams.skip! = searchParams.size! * (page - 1)

  handleSearchClick(currentFilterParams.value, 'pc')
}

const handlePCFilterSortChange = (sortBy: number) => {  
  searchParams.sortBy = sortBy

  handleSearchClick({ ...currentFilterParams.value, sortBy }, 'pc')
}

const PCSearchResultRef = ref<any>(null)
const handlePCFilterKeywordChange = async(keywords: string) => {  
  if(!keywords) {
    showToast('Please enter keywords first.')
    return
  }

  if(!isMobile.value) {
    const query = route.query
    if(query.source) {
      const [m, t] = (query.source as string).split('|')

      query.source = `${m}|${t}|${keywords}`
    }
    
    delete query.correctionWords

    const newUrl = router.resolve({ path: route.path, query: { q: keywords || '', page: 1, ...query }}).href

    location.replace(newUrl)
  } else {
    searchParams.keywords = keywords
    Object.keys(searchParams).forEach((k: any) => {
      delete searchParams[k as keyof ISearchParams]
    })
    Object.assign(
      searchParams,
      {
        skip: 0,
        size: 36,
        q: keywords
      }
    )

    await handleSearchClick(searchParams, 'refresh')

    PCSearchResultRef.value && PCSearchResultRef.value.clearFilter()
  }
}
/** ------ PC END ----------- */

try {
  await handleSearchClick({}, isMobile.value ? 'refresh' : 'pc')
} catch(err) {
  console.error(err)
}

const seoConfig = useGlobalJsonData().value.seo?.data
/** SEO */
const setSEOMeta = (isCategory: boolean) => {
  if(!seoConfig) { return }

  const _config = isCategory ? seoConfig.categoryResult : seoConfig.searchResult
  if(!_config) { return }

  let varStr: string = searchParams.keywords || ''

  /** 类目页处理 */
  if(isCategory) {
    varStr = resolveCategoryDisplayName()
  }

  const formatSeoStr = (str?: string): string => (str || '')
      .replaceAll('$[searchKeyword]', varStr)
      .replaceAll('$[productSubCategory]', varStr)

  const { title: seoTitle, metaDescription, metaKeywords, metaTitle } = _config
  const metaObj = [
    { name: 'description', content: formatSeoStr(metaDescription) },
    { name: 'keywords', content: formatSeoStr(metaKeywords) },
    { name: 'title', content: formatSeoStr(metaTitle) }
  ]
  if (!!(source || externalLastTrafficSourceType) || route.query.page) {
    metaObj.push({ name: 'robots', content: 'noindex,follow' })
  }
  return {
    title: formatSeoStr(seoTitle),
    meta: metaObj
  }
}

const breadcrumbs = ref<IBreadcrumbs[]>([{ name: 'Home', id: 1, path: '/' }])
if(!/^\/category/.test(route.path)) {
  breadcrumbs.value.push({ name: (route.query.q || 'Search Result') as string, id: 1, path: '' })
}
const headers = useRequestHeaders()
const proto = headers['x-forwarded-proto'] || 'https'
const host = `${proto}://${process.server ? headers.host : window.location.host}`
const setStructuredData = async(): Promise<string | undefined> => {
  let fn: { fn: (id: number)=> Promise<any>, id: number } | null = null

  if(backCid) {
    fn = { fn: getCategoryPathById, id: Number(backCid.split('_')[0]) }
  }
  if(searchParams.frontCategoryId) {
    fn = { fn: getFrontCategoryPathById, id: searchParams.frontCategoryId }
  }

  if(!fn) {
    return JSON.stringify({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "name": "Search Category Breadcrumb",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Home",
          "item": host
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": searchParams.keywords  || '',
          "item": `${host}/search?q=${searchParams.keywords || ''}`
        }
      ]
    })
  }

  const res = await fn.fn(fn.id)
  const breadcrumbCtgs: IBreadcrumbs[] = []
  const structuredItems:{ name: string, position: number, item: string, '@type': 'ListItem' }[] = [];
  
  (res || []).forEach((c: { id: number, name: string }, index: number) => {
    const { id, name } =c
    const path = `${host}/category/${$formatStringToURL(c.name)}?id=${c.id}&form=category`

    breadcrumbCtgs.push({ name, id: id, path })

    structuredItems.push({ name,  position: index + 2, item: path, '@type': 'ListItem' })
  });

  breadcrumbs.value.push(...breadcrumbCtgs)
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "name": "Search Keyword Breadcrumb",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": host
      },
      ...structuredItems
    ]
  }

  const structuredDataStr = JSON.stringify(structuredData)

  return structuredDataStr  
}

const isCategorySearch = () => {
  return route.query.form === 'category' || !!(backCid || searchParams.frontCategoryId)
}

/** 类目搜索展示名：优先 route param，其次 breadcrumb（/search?id= 等无 param 场景） */
const resolveCategoryDisplayName = (): string => {
  const paramName = route.params.categoryName as string
  if (paramName && paramName !== 'all') {
    return paramName
  }

  const lastCtgCrumb = [...breadcrumbs.value].reverse().find(item => item.path && item.id !== 1)
  return lastCtgCrumb?.name || ''
}

const buildH1Tips = (): string => {
  if (isCategorySearch()) {
    const ctgName = resolveCategoryDisplayName()
    return ctgName ? `${searchResult.value.total} results for ${ctgName}.` : ''
  }
  return `${searchResult.value.total} results for <span style="color:#000000">${searchParams.keywords}</span>`
}

const setCanonical = () => {
  // 从原始 URL 的 search 字符串中提取 q 参数，避免解码 + 号
  const requestUrl = useRequestURL()
  const qMatch = requestUrl.search.match(/[?&]q=([^&]*)/)
  const rawQ = qMatch ? qMatch[1] : ''
  let canonicalHref = `${host}/search?q=${rawQ}`

  if(isCategorySearch()) {
    canonicalHref = `${host}/category/${encodeURIComponent((route.params.categoryName || '') as string)}`

    if(backCid) {
      canonicalHref += `?backCid=${backCid}&form=category`
    }
    if(searchParams.frontCategoryId) {
      canonicalHref += `?id=${searchParams.frontCategoryId}&form=category`
    }
  }

  return {
    link: [
      {
        rel: 'canonical',
        href: canonicalHref
      }
    ]
  }
}

const h1Tips = computed(() => buildH1Tips())

// SEO 相关内容
try {
  const canonical = setCanonical()

  const script = []
  try {
    const structuredDataStr = await setStructuredData()

    if(structuredDataStr) {
      script.push({ type: 'application/ld+json', innerHTML: structuredDataStr })
    }
  } catch(e) {
    console.error(e)
  }

  let seoMeta
  try {
    seoMeta = setSEOMeta(isCategorySearch())
  } catch(e) {
    console.error('[search] setSEOMeta failed:', e)
  }

  useHead({
    title: seoMeta?.title || '',
    meta: [
      ...seoMeta?.meta || [],
    ],
    script: [ ...script ],
    link: [...canonical.link]
  })
} catch(e) {
  console.error(e)
}
</script>

<style lang="scss" scoped>
.search-header{
  position: fixed;
  top: 82px;
  left: 0;
  right: 0;
  background-color: #fff;
  z-index: 99;
  :deep(.van-dropdown-menu__bar) {
    box-shadow: none;
  }
  :deep(.van-dropdown-menu__bar) {
    div {
      justify-content: left;
    }
    :last-child {
      justify-content: right;
    }
  }
}
.correction-words-tips{
  color: #666;
  font-size: 24px;
  line-height: 34px;
  margin: 15px 20px;
  i{ font-size: 28px; }
  .first-word{ color: var(--default_color); }
  .last-word{ color: var(--default_color);border-bottom: 1px solid var(--default_color); }
}
.product-list-wrapper{
  padding-top: 180px;
  .category-div {
    margin-bottom: 4px;
  }
}

.product-list{
  display: flex;
  flex-wrap: wrap;
  padding: 0 5px;
  .product{
    width: 50%;
    box-sizing: border-box;
    padding: 5px;
  }
}
</style>
